<template>
  <v-col cols="12" class="pa-0">
    <div v-for="(section, i) in items" :key="i">
      <AddSpecificationItem
        :loading="loading"
        :section="section[0].section"
        :sections="sections"
        :section-position="section[0].sectionPosition"
        :is-delete="getTestSection(section)"
        @create-specification-item="createSpecificationItem"
        @delete-section="showSectionConfirmDialog(section)"
        @update-section="updateSectionName(section, $event)"
      />
      <v-data-table
        ref="myTable"
        v-model="selected"
        class="mb-5"
        :loading="loadingTable"
        :headers="itemsHeaders"
        :items="section"
        :items-per-page="-1"
        fixed-header
        :height="section.length > 5 ? '300' : 'auto'"
        hide-default-header
        hide-default-footer
        item-key="name"
      >
        <template v-slot:header="{ props: { headers } }">
          <thead>
            <tr>
              <th
                v-for="(header, index) in headers"
                :key="index"
                :style="header.style"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <span v-on="header.tooltip ? on : false">{{
                      header.text
                    }}</span>
                  </template>
                  <span style="display: block;max-width: 350px;">{{
                    header.tooltip
                  }}</span>
                </v-tooltip>
              </th>
            </tr>
          </thead>
        </template>
        <template #body="props">
          <draggable
            :list="props.items"
            tag="tbody"
            :move="moveDragItem"
            :clone="cloneDragItem"
          >
            <DataTableRowHandler
              v-for="(item, index) in props.items"
              :key="index"
              :item="item"
              :headers="itemsHeaders"
              :actions="actions"
              @updateTitle="updateItemTitle"
              @updateEstimate="updateItemEstimate"
              @updateTrack="updateItemTrack"
              @showSectionDialog="showSectionDialog"
              @showSectionItemConfirmDialog="showSectionItemConfirmDialog"
              @updateTestIndex="updateTestIndex"
            />
          </draggable>
        </template>
      </v-data-table>
    </div>
    <SectionDialog
      :dialog="sectionDialogMode"
      :current-item="currentItem"
      :sections="sections"
      @cancel="closeSectionDialog"
      @update="updateItemSection"
    />
    <ConfirmDialog
      v-model="sectionConfirmDialog"
      max-width="375"
      title="Видалити розділ з завданнями?"
      @cancel="closeSectionConfirmDialog"
      @confirm="deleteSection"
    />
    <ConfirmDialog
      v-model="sectionItemConfirmDialog"
      max-width="325"
      title="Видалити завдання?"
      @cancel="closeSectionItemConfirmDialog"
      @confirm="deleteSectionItem"
    />
  </v-col>
</template>

<script>
import * as specificationsApi from "../../../backend/specificationsApi.js";
import AddSpecificationItem from "./AddSpecificationItem.vue";
import MenuRow from "../../../components/MenuRow.vue";
import SectionDialog from "../../../components/SectionDialog.vue";
import DecimalEditor from "../../../components/DecimalEditor.vue";
import ConfirmDialog from "../../../components/ConfirmDialog.vue";
import DataTableRowHandler from "./DataTableRowHandler.vue";

import itemsHeaders from "../../../helpers/itemsHeaders.js";
import { ERRORS } from "@/helpers";
import { required } from "vuelidate/lib/validators";
import draggable from "vuedraggable";

export default {
  components: {
    AddSpecificationItem,
    MenuRow,
    SectionDialog,
    DecimalEditor,
    ConfirmDialog,
    draggable,
    DataTableRowHandler,
  },
  directives: {
    focus: {
      inserted(el) {
        el.focus();
      },
    },
  },
  props: ["id", "items", "sections", "loadingTable"],
  data: () => ({
    loading: false,
    sectionDialogMode: false,
    loadingEstimate: false,
    currentItem: [],
    sectionItemConfirmDialog: false,
    itemsForDelete: [],
    sectionConfirmDialog: false,
    editItem: {},
    actions: [
      {
        title: "Перемістити в інший розділ",
        icon: "mdi-arrow-right",
        method: "show-section-dialog",
      },
      {
        title: "Видалити",
        icon: "mdi-delete",
        method: "show-section-item-delete-dialog",
      },
    ],
    selected: [],
  }),
  validations() {
    return {
      editItem: {
        title: {
          required,
        },
        estimateMin: {
          required,
          minValue: (value) => 0 < value,
          maxValue: (value) => value < Number(this.editItem.estimateNorm),
        },
        estimateNorm: {
          required,
          minValue: (value) => 0 < value,
          maxValue: (value) => value < Number(this.editItem.estimateMax),
        },
      },
    };
  },
  computed: {
    itemsHeaders() {
      return itemsHeaders.sectionHeaders;
    },
  },
  methods: {
    async createSpecificationItem(item) {
      this.loading = true;
      await specificationsApi.createSpecificationItem(this.id, item);
      this.loading = false;
      this.$emit("get-specification");
    },
    async updateItemTitle(item) {
      this.loading = true;
      await specificationsApi.updateItemTitle(this.id, item.id, item.title);
      this.$emit("get-specification");
      this.loading = false;
    },
    async updateItemSection(itemInfo) {
      const item = itemInfo[0];
      const section = itemInfo[1];
      if (section == "" || section == item.section) {
        return;
      }
      this.loading = true;
      await specificationsApi.updateItemSection(this.id, item.id, section);
      this.sectionDialogMode = false;
      this.loading = false;
      this.$emit("get-specification");
    },
    roundingNumber(num) {
      return parseFloat(parseFloat(num).toFixed(1));
    },
    async updateItemEstimate(item) {
      this.loadingEstimate = true;
      const estimate = {
        avgDev: this.roundingNumber(item.estimate.avgDev),
        minDev: this.roundingNumber(item.estimate.minDev),
        maxDev: this.roundingNumber(item.estimate.maxDev),
        avgAnalyst: this.roundingNumber(item.estimate.avgAnalyst),
        minAnalyst: this.roundingNumber(item.estimate.minAnalyst),
        maxAnalyst: this.roundingNumber(item.estimate.maxAnalyst),
      };
      try {
        await specificationsApi.updateItemEstimate(this.id, item.id, estimate);
        this.$notifySuccess();
      } catch (error) {
        this.$notifyWarning(error.response.data);
      } finally {
        this.loadingEstimate = false;
        this.$emit("get-specification");
      }
    },
    async updateItemTrack(item) {
      this.loading = true;
      await specificationsApi.updateItemTrack(
        this.id,
        item.id,
        item.issueJiraDev,
        item.issueJiraAnalyst
      );
      this.$emit("get-specification");
      this.loading = false;
    },
    async updateSectionName(section, $event) {
      this.loading = true;
      const currentSection = $event.currentSection;
      const newSection = $event.newSection;
      await specificationsApi.updateSectionName(
        this.id,
        newSection,
        currentSection
      );
      this.$emit("get-specification");
      this.loading = false;
    },
    showSectionDialog(item) {
      this.sectionDialogMode = true;
      this.currentItem = item;
    },
    closeSectionDialog() {
      this.sectionDialogMode = false;
    },
    showSectionItemConfirmDialog(item) {
      this.itemsForDelete.push(item);
      this.sectionItemConfirmDialog = true;
    },
    closeSectionItemConfirmDialog() {
      this.itemsForDelete = [];
      this.sectionItemConfirmDialog = false;
    },
    async deleteSectionItem() {
      await specificationsApi.deleteSpecificationItem(
        this.id,
        this.itemsForDelete[0].id
      );
      this.sectionItemConfirmDialog = false;
      this.$notifySuccess("Завдання видалено");
      this.$emit("get-specification");
      this.itemsForDelete = [];
    },
    showSectionConfirmDialog(section) {
      this.itemsForDelete = section;
      this.sectionConfirmDialog = true;
    },
    closeSectionConfirmDialog() {
      this.itemsForDelete = [];
      this.sectionConfirmDialog = false;
    },
    async deleteSection() {
      await specificationsApi.deleteSpecificationSection(
        this.id,
        this.itemsForDelete[0].section
      );
      this.sectionConfirmDialog = false;
      this.$notifySuccess("Розділ видалено");
      this.$emit("get-specification");
      this.itemsForDelete = [];
    },
    checkTestSection(items) {
      return !!items.find((i) => i.isTestSection);
    },
    cloneDragItem(item) {
      const cloneMe = JSON.parse(JSON.stringify(item));
      return cloneMe;
    },
    async moveDragItem(evt) {
      const item = evt.draggedContext.element;
      const itemIndex = evt.draggedContext.futureIndex + 1;

      await specificationsApi.changePositionSectionItem(
        this.id,
        item.id,
        itemIndex
      );
      this.$emit("get-specification");
    },
    getTestSection(items) {
      return !items.find((i) => i.isTestSection);
    },
    async updateTestIndex(item) {
      this.loading = true;
      await specificationsApi.changeTestIndex(this.id, +item.testIndex);
      this.$emit("get-specification");
      this.loading = false;
    },
  },
};
</script>
